import { type RefObject, useEffect, useRef } from 'react'

type UseLingerIntersection = (params: {
  /** The element to observe (e.g. a "Show more" button). */
  targetRef: RefObject<HTMLElement | null>
  /** The scroll container that defines the viewport for intersection checks. */
  rootRef: RefObject<HTMLElement | null>
  /** How long the target must stay intersecting before `onLinger` fires (ms). */
  delayMs: number
  /** Called when the target has been intersecting for `delayMs` uninterrupted. */
  onLinger: () => void
  /** Disable the observer entirely (e.g. before paging is allowed). */
  enabled: boolean
}) => void

/**
 * Fires `onLinger` when `targetRef` enters `rootRef`'s viewport AND stays there
 * for at least `delayMs`. A brief flyby (e.g. macOS momentum scroll that
 * briefly exposes the target before settling elsewhere) won't trigger it —
 * the timer is cancelled the moment the target leaves the viewport.
 *
 * Typical use: debounced infinite scroll. The "Show more" button only auto-
 * triggers when the user actually settled at the top, not while they're
 * mid-flick.
 */
export const useLingerIntersection: UseLingerIntersection = ({
  targetRef,
  rootRef,
  delayMs,
  onLinger,
  enabled,
}) => {
  // Stash the callback in a ref so changes to its identity (callers often
  // pass inline arrows) don't tear down the observer and reset the timer.
  const onLingerRef = useRef(onLinger)
  onLingerRef.current = onLinger

  useEffect(() => {
    if (!enabled) {
      return
    }
    const target = targetRef.current
    const root = rootRef.current
    if (!target || !root) {
      return
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const observer = new IntersectionObserver(
      entries => {
        const isIntersecting = entries[0]?.isIntersecting ?? false
        if (isIntersecting && timeoutId === null) {
          timeoutId = setTimeout(() => {
            timeoutId = null
            onLingerRef.current()
          }, delayMs)
          return
        }
        if (!isIntersecting && timeoutId !== null) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      },
      { root }
    )
    observer.observe(target)
    return () => {
      observer.disconnect()
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }, [enabled, delayMs, targetRef, rootRef])
}
